import React from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from "./Modal.tsx";
import {ErrorMessage} from "./dynamicforms/dyanimcFormUtil.tsx";

interface WarningConfirmationModalProps {
    isOpen: boolean;
    warnings: ErrorMessage[]; // Expecting only warnings here
    onConfirm: () => void;
    onClose: () => void; // This will act as "Cancel"
}

export const WarningConfirmationModal: React.FC<WarningConfirmationModalProps> = ({
                                                                                      isOpen,
                                                                                      onConfirm,
                                                                                      onClose,
                                                                                      warnings
                                                                                  }) => {


    if (!isOpen) {
        return null;
    }

    // Function to scroll to an element by ID (optional, but good for UX)
    const scrollToField = (fieldKey: string) => {
        const element = document.getElementById(fieldKey);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus({ preventScroll: true });
            // Optional: Add a temporary highlight class
            element.classList.add('warning-highlight-scroll');
            setTimeout(() => element.classList.remove('warning-highlight-scroll'), 2500);
        }
    };


    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Submission Warnings"
            maxWidth="md"
        >
            {/* Add temporary highlight style if you use scrollToField */}
            <style>
                {`.warning-highlight-scroll {
                    outline: 2px solid #f59e0b; /* amber-500 */
                    box-shadow: 0 0 8px #f59e0b;
                }`}
            </style>
            <div className="mb-4">
                <div className="flex items-start p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
                    <AlertTriangle className="h-6 w-6 text-yellow-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-sm text-yellow-700">
                        Your form has the following warnings. Please review them.
                        You can choose to proceed with submission or cancel to make changes.
                    </p>
                </div>
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto mb-6 pr-2">
                {warnings.map((warning, index) => (
                    <div key={`${warning.fieldKey}-${index}`} className="p-2 border border-yellow-200 bg-yellow-50 rounded-md">
                        <strong
                            className="text-sm text-yellow-800 cursor-pointer hover:underline"
                            onClick={() => scrollToField(warning.fieldKey)}
                            title={`Go to field: ${warning.label}`}
                        >
                            {warning.label || warning.fieldKey}:
                        </strong>
                        <p className="text-xs text-yellow-700 ml-1">{warning.message}</p>
                    </div>
                ))}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md border border-slate-300 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                    Proceed Anyway
                </button>
            </div>
        </Modal>
    );
};